import React from "react";
import * as FiIcons from "react-icons/fi";
import SafeIcon from "../../../common/SafeIcon";

const { FiCheckCircle, FiCreditCard, FiRefreshCw, FiShield, FiXCircle } = FiIcons;

function formatBalance(value) {
  const balance = Number(value);
  return Number.isFinite(balance)
    ? balance.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";
}

export default function ClientBankAccountCard({
  account,
  syncingAccounts,
  syncingTransactions,
  onSyncAccounts,
  onSyncTransactions,
  currency,
}) {
  const mapped = Boolean(account.account_id);
  const itemIdAvailable = Boolean(account.plaid_item_id);

  return (
    <article className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
            <SafeIcon icon={FiCreditCard} className="text-xl" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate font-bold text-slate-900">
              {account.account_name || "Bank account"}
            </h2>
            <p className="mt-1 truncate text-sm text-slate-500">
              {account.institution_name || "Financial institution"}
              {account.mask ? ` ···· ${account.mask}` : ""}
            </p>
            <p className="mt-2 text-xs capitalize text-slate-500">
              {String(account.account_type || "account").replaceAll("_", " ")}
            </p>
          </div>
        </div>
        <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
          <SafeIcon icon={FiCheckCircle} />
          Active connection
        </span>
      </div>

      <div className="grid gap-4 border-y border-slate-100 px-5 py-4 sm:grid-cols-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Reported balance</p>
          <p className="mt-1 font-bold text-slate-900">{formatBalance(account.balance)}</p>
          <p className="mt-1 text-xs text-slate-400">
            Amount as supplied by the bank; no currency conversion.
            {currency ? ` Organization currency: ${currency}.` : ""}
          </p>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">ProfitYo account</p>
          <p className={`mt-1 inline-flex items-center gap-1.5 text-sm font-semibold ${mapped ? "text-emerald-700" : "text-amber-700"}`}>
            <SafeIcon icon={mapped ? FiCheckCircle : FiXCircle} />
            {mapped ? "Mapped" : "Not Mapped"}
          </p>
          {!mapped && (
            <p className="mt-1 text-xs text-slate-500">
              Ask your accounting team to map this account before syncing transactions.
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Last synchronized</p>
          <p className="mt-1 text-sm font-semibold text-slate-800">
            {account.last_sync_at
              ? new Date(account.last_sync_at).toLocaleString()
              : "Not available"}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 p-5">
        <button
          type="button"
          onClick={() => onSyncAccounts(account)}
          disabled={!itemIdAvailable || syncingAccounts}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={!itemIdAvailable ? "Plaid Item ID is unavailable from this account record." : undefined}
        >
          <SafeIcon icon={FiRefreshCw} className={syncingAccounts ? "animate-spin" : ""} />
          {syncingAccounts ? "Syncing accounts…" : "Sync Accounts"}
        </button>
        <button
          type="button"
          onClick={() => onSyncTransactions(account)}
          disabled={!mapped || !itemIdAvailable || syncingTransactions}
          className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          title={!mapped ? "Map this bank account before syncing transactions." : undefined}
        >
          <SafeIcon icon={FiRefreshCw} className={syncingTransactions ? "animate-spin" : ""} />
          {syncingTransactions ? "Syncing transactions…" : "Sync Transactions"}
        </button>
      </div>
      {!itemIdAvailable && (
        <p className="flex items-start gap-2 px-5 pb-5 text-xs text-amber-800">
          <SafeIcon icon={FiShield} className="mt-0.5 shrink-0" />
          Plaid Item ID is not available in the permitted bank-account data. Sync actions need an authorized backend source for that ID.
        </p>
      )}
    </article>
  );
}